import BigInt from 'big-integer';
import { getAddress } from 'viem';
import bs58 from 'bs58';
// @ts-ignore
import crypto from 'crypto-browserify';
import {
  NetworkConfig,
  MintDepositAddress,
  BurnHistoryItem,
  AuthorityNode,
  BridgeFeesConfig,
} from '../types/bridge';

const DECIMALS = 8;

export const toSatoshi = (x: number): string => {
  // Handle edge cases
  if (isNaN(x) || !isFinite(x)) {
    return '0';
  }
  
  // Convert to fixed decimal string to avoid scientific notation
  let xs = x.toFixed(DECIMALS);
  
  // Remove the decimal point by concatenating integer and fractional parts
  const decimalIndex = xs.indexOf('.');
  if (decimalIndex !== -1) {
    xs = xs.substr(0, decimalIndex) + xs.slice(decimalIndex + 1);
  }
  
  // Remove leading zeros but keep at least one digit
  xs = xs.replace(/^0+/, '') || '0';
  
  return xs;
};

export const fromSatoshi = (xs: string): string => {
  // Convert an integer satoshi string to a full DINGO decimal string. Returns a
  // plain numeric string (no thousands separators) so callers can parseFloat()
  // it for math; trailing fractional zeros are trimmed. (The previous version
  // dropped the fractional part and comma-grouped the integer, so sub-1-DINGO
  // values like the validator fee parsed to wildly wrong numbers, e.g. a
  // 0.0125 fee rendered as "125,000" → parseFloat → 125.)
  if (xs === null || xs === undefined || xs === "") {
    return "0";
  }
  const neg = xs[0] === "-";
  let digits = (neg ? xs.slice(1) : xs).replace(/[^0-9]/g, "");
  digits = digits.replace(/^0+/, "") || "0";
  const padded = digits.padStart(DECIMALS + 1, "0");
  const integer = padded.slice(0, padded.length - DECIMALS);
  const fractional = padded.slice(padded.length - DECIMALS).replace(/0+$/, "");
  const value = fractional ? `${integer}.${fractional}` : integer;
  return value === "0" ? "0" : (neg ? `-${value}` : value);
};

export const numberWithCommas = (x: string): string => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const sha256 = (data: string | Buffer): Buffer => {
  return crypto.createHash("sha256").update(data).digest();
};

export const isValidDingocoinAddress = (address: string, isTestnet?: boolean): boolean => {
  try {
    const raw = bs58.decode(address);
    if (raw.length !== 25) {
      return false;
    }
    // Accepted base58check version bytes:
    //   mainnet P2PKH 0x1e (D…), mainnet P2SH 0x16 (A…)
    //   testnet P2PKH 0x71 (n…), testnet P2SH 0xc4 (2…)
    // When the active network is known (isTestnet), restrict to that network's
    // bytes so a mainnet address can't be entered on a testnet bridge (and vice
    // versa); when unknown, accept both. Either way the daemon's
    // dingo.verifyAddress (RPC validateaddress) remains the authoritative check —
    // this is just a client-side pre-filter.
    const MAINNET_VERSIONS = [0x1e, 0x16];
    const TESTNET_VERSIONS = [0x71, 0xc4];
    const ACCEPTED_VERSIONS =
      isTestnet === true
        ? TESTNET_VERSIONS
        : isTestnet === false
        ? MAINNET_VERSIONS
        : [...MAINNET_VERSIONS, ...TESTNET_VERSIONS];
    if (!ACCEPTED_VERSIONS.includes(raw[0])) {
      return false;
    }
    const checksum = sha256(sha256(Buffer.from(raw.slice(0, 21))));
    return Buffer.compare(Buffer.from(raw.slice(21, 25)), checksum.slice(0, 4)) === 0;
  } catch {
    return false;
  }
};

export const authorityBaseUrl = (network: NetworkConfig, node: AuthorityNode): string => {
  const scheme = node.httpScheme ?? network.authorityHttpScheme ?? 'https';
  return `${scheme}://${node.location}:${node.port}${node.path ?? ''}`;
};

export const post = async (link: string, data: any = {}): Promise<any> => {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), 5000);
  
  const response = await fetch(link, {
    method: 'POST',
    signal: controller.signal,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

/** Required on authority POST routes (middleware reads `body.network`). */
export function authorityPostBody(
  network: NetworkConfig,
  body: Record<string, unknown> = {}
): Record<string, unknown> {
  return { ...body, network: network.evmNetworkSlug };
}

/**
 * Minimum reachable authorities before pool deposit assignment:
 * max(majority of fleet, authorityThreshold), capped at N — aligns UX with query/mint quorum across future topologies.
 * All contacted nodes must still unanimously agree on reserve/register.
 */
export function minAuthorityNodesOnlineForAssignment(network: NetworkConfig): number {
  const total = network.authorityNodes.length;
  if (total <= 0) return 0;
  const majorityHalfCeil = Math.ceil(total * 0.5);
  const t = network.authorityThreshold;
  return Math.min(total, Math.max(majorityHalfCeil, t));
}

export function hasEnoughAuthorityNodesForAssignment(
  network: NetworkConfig,
  aliveCount: number
): boolean {
  return aliveCount >= minAuthorityNodesOnlineForAssignment(network);
}

export function notEnoughAuthorityNodesForAssignmentMessage(
  network: NetworkConfig,
  aliveCount: number
): string {
  const need = minAuthorityNodesOnlineForAssignment(network);
  const total = network.authorityNodes.length;
  return `At least ${need} of ${total} authority nodes must be online (majority and consensus threshold; currently ${aliveCount}).`;
}

export const getAliveNodes = async (network: NetworkConfig): Promise<number[]> => {
  const alive: number[] = [];
  
  for (const i in network.authorityNodes) {
    try {
      await post(
        `${authorityBaseUrl(network, network.authorityNodes[i])}/ping`,
        authorityPostBody(network, {})
      );
      alive.push(parseInt(i));
    } catch {
      // Node is down, skip
    }
  }
  
  return alive;
};

export const getRandomAuthorityLink = (network: NetworkConfig, aliveNodes?: number[]): string => {
  const all = network.authorityNodes.map((_, i) => i);
  let pool =
    aliveNodes && aliveNodes.length > 0
      ? aliveNodes.filter((i) => i >= 0 && i < network.authorityNodes.length)
      : all;
  if (pool.length === 0) pool = all;
  const nodeIndex = pool[Math.floor(Math.random() * pool.length)];
  const node = network.authorityNodes[nodeIndex];
  return authorityBaseUrl(network, node);
};

export const getStableAuthorityLink = (network: NetworkConfig): string => {
  const last = network.authorityNodes.length - 1;
  const node = network.authorityNodes[last];
  return authorityBaseUrl(network, node);
};

export const formatAmount = (amount: string, decimals: number = DECIMALS): string => {
  // Handle empty or invalid input
  if (!amount || amount === '') {
    return '0';
  }
  
  // Convert scientific notation to decimal string
  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount)) {
    return '0';
  }
  
  // If the amount is already in satoshi format (integer string), convert to a
  // decimal DINGO value and thousands-group the integer part for display.
  if (amount.indexOf('.') === -1 && amount.indexOf('e') === -1 && amount.indexOf('E') === -1) {
    const dec = fromSatoshi(amount);
    const neg = dec.startsWith('-');
    const [int, frac] = (neg ? dec.slice(1) : dec).split('.');
    const grouped = numberWithCommas(int);
    return `${neg ? '-' : ''}${frac ? `${grouped}.${frac}` : grouped}`;
  }

  // Otherwise, it's a decimal amount, so format it directly
  return numericAmount.toFixed(8).replace(/\.?0+$/, '');
};

export const parseAmount = (amount: string): bigint => {
  const satoshiAmount = toSatoshi(parseFloat(amount));
  return globalThis.BigInt(satoshiAmount);
};

/** Default matches authority daemon (1250 ppm = 0.125% each way). */
export const DEFAULT_VALIDATOR_FEE_PPM = 1250;
export const MAX_VALIDATOR_FEE_PPM = 10000;

/** Display:1250 -> "0.125%" (10000 ppm = 1%). */
export function ppmToPercentLabel(ppm: number): string {
  const x = ppm / 10000;
  const s = x.toFixed(4).replace(/\.?0+$/, '');
  return `${s}%`;
}

/** Fee in whole DINGO string (8 decimals), matching on-chain satoshi rounding. */
export function calculateValidatorFeeDingo(amount: string, feePpm: number): string {
  if (!amount || !Number.isFinite(parseFloat(amount)) || parseFloat(amount) <= 0 || feePpm <= 0) {
    return '0';
  }
  const sats = toSatoshi(parseFloat(amount));
  const feeSats =
    (globalThis.BigInt(sats) * globalThis.BigInt(feePpm)) / globalThis.BigInt(1000000);
  return fromSatoshi(feeSats.toString());
}

export async function fetchBridgeFeesConfig(
  network: NetworkConfig
): Promise<BridgeFeesConfig> {
  try {
    const link = getStableAuthorityLink(network);
    const res = await post(`${link}/stats`, authorityPostBody(network, {}));
    const bf = res.data?.bridgeFees;
    if (bf && typeof bf.validatorFeePpm === 'number') {
      return {
        validatorFeePpm: bf.validatorFeePpm,
        validatorFeeMaxPpmEachWay:
          typeof bf.validatorFeeMaxPpmEachWay === 'number'
            ? bf.validatorFeeMaxPpmEachWay
            : MAX_VALIDATOR_FEE_PPM,
        validatorFeePercentEachWay:
          typeof bf.validatorFeePercentEachWay === 'number' ? bf.validatorFeePercentEachWay : undefined,
      };
    }
  } catch {
    /* use defaults */
  }
  return {
    validatorFeePpm: DEFAULT_VALIDATOR_FEE_PPM,
    validatorFeeMaxPpmEachWay: MAX_VALIDATOR_FEE_PPM,
    validatorFeePercentEachWay: DEFAULT_VALIDATOR_FEE_PPM / 10000,
  };
}

/** Must match `smartContract.buildPoolDepositClaimMessage` / authority `registerMintDepositFromPool`. */
export function buildPoolDepositClaimMessage(
  evmNetworkSlug: string,
  chainId: number,
  mintAddress: string,
  poolSequence: number,
  multisigDepositAddress: string
): string {
  const n = String(evmNetworkSlug != null ? evmNetworkSlug : '').trim();
  const c = String(Number(chainId));
  const m = getAddress(mintAddress.trim() as `0x${string}`);
  const p = String(Number(poolSequence));
  const d = String(multisigDepositAddress).trim();
  return `wDingocoin:claimPoolDeposit:v2|network=${n}|chainId=${c}|mint=${m}|pool=${p}|deposit=${d}`;
}

function parsePoolQueryData(d: Record<string, unknown> | undefined | null): {
  poolSequence: number | null;
  multisigDepositAddress: string | null;
  chainId: number | null;
  reservedUntil: number | null;
} {
  if (!d) {
    return { poolSequence: null, multisigDepositAddress: null, chainId: null, reservedUntil: null };
  }
  const ps = d.poolSequence;
  const poolSequence = ps === null || ps === undefined ? null : Number(ps);
  const addr = d.multisigDepositAddress;
  const multisigDepositAddress =
    addr === null || addr === undefined || addr === '' ? null : String(addr);
  const cid = d.chainId;
  const chainId = cid === null || cid === undefined ? null : Number(cid);
  const ru = d.reservedUntil;
  const reservedUntil = ru === null || ru === undefined ? null : Number(ru);
  return { poolSequence, multisigDepositAddress, chainId, reservedUntil };
}

/** Requires every queried node to return the same next pool row (authorities must keep pool DBs aligned). */
export async function queryNextPoolDepositWithConsensus(
  network: NetworkConfig,
  aliveNodeIndices: number[]
): Promise<{ poolSequence: number; multisigDepositAddress: string } | null> {
  if (aliveNodeIndices.length === 0) {
    throw new Error('No authority nodes to query');
  }
  const responses = await Promise.all(
    aliveNodeIndices.map((i) =>
      post(
        `${authorityBaseUrl(network, network.authorityNodes[i])}/queryNextUnassignedPoolDeposit`,
        authorityPostBody(network, {})
      )
    )
  );
  const parsed = responses.map((r) => parsePoolQueryData(r.data as Record<string, unknown>));
  const first = parsed[0];
  const same = parsed.every(
    (p) =>
      p.poolSequence === first.poolSequence &&
      p.multisigDepositAddress === first.multisigDepositAddress &&
      p.chainId === first.chainId &&
      p.reservedUntil === first.reservedUntil
  );
  if (!same) {
    throw new Error('Authority nodes disagree on the next pool deposit; retry or check node sync');
  }
  if (first.chainId !== null && first.chainId !== network.chainId) {
    throw new Error('Pool query chainId does not match selected network');
  }
  if (first.poolSequence === null || first.multisigDepositAddress === null) {
    return null;
  }
  return { poolSequence: first.poolSequence, multisigDepositAddress: first.multisigDepositAddress };
}

/** Reserve a pool slot per mint on every authority; all must agree on sequence and deposit address. */
export async function reservePoolDepositWithConsensus(
  network: NetworkConfig,
  aliveNodeIndices: number[],
  mintAddress: string
): Promise<{ poolSequence: number; multisigDepositAddress: string } | null> {
  if (aliveNodeIndices.length === 0) {
    throw new Error('No authority nodes to query');
  }
  const responses = await Promise.all(
    aliveNodeIndices.map((i) =>
      post(
        `${authorityBaseUrl(network, network.authorityNodes[i])}/reservePoolDeposit`,
        authorityPostBody(network, { mintAddress })
      )
    )
  );
  const parsed = responses.map((r) => parsePoolQueryData(r.data as Record<string, unknown>));
  const first = parsed[0];
  // Consensus is on the slot identity (sequence + multisig + chain). reservedUntil is a
  // per-node TTL (now + POOL_RESERVE_TTL_SEC) computed independently, so it can legitimately
  // differ by a second across nodes — requiring it to match byte-exactly would make a
  // sub-second clock skew permanently break this mint address (the reservation is idempotent).
  const same = parsed.every(
    (p) =>
      p.poolSequence === first.poolSequence &&
      p.multisigDepositAddress === first.multisigDepositAddress &&
      p.chainId === first.chainId
  );
  if (!same) {
    throw new Error('Authority nodes disagree on pool reservation; retry');
  }
  if (first.chainId !== null && first.chainId !== network.chainId) {
    throw new Error('Pool reservation chainId does not match selected network');
  }
  if (first.poolSequence === null || first.multisigDepositAddress === null) {
    return null;
  }
  return { poolSequence: first.poolSequence, multisigDepositAddress: first.multisigDepositAddress };
}

export async function registerMintDepositFromPoolAllAuthorities(
  network: NetworkConfig,
  aliveNodeIndices: number[],
  mintAddress: string,
  poolSequence: number,
  multisigDepositAddress: string,
  claimSignature: string
): Promise<string> {
  const results = await Promise.all(
    aliveNodeIndices.map((i) =>
      post(
        `${authorityBaseUrl(network, network.authorityNodes[i])}/registerMintDepositFromPool`,
        authorityPostBody(network, {
          mintAddress,
          poolSequence,
          multisigDepositAddress,
          claimSignature,
        })
      )
    )
  );
  const deposit = results[0].data?.depositAddress as string | undefined;
  if (!deposit || !results.every((r) => r.data?.depositAddress === deposit)) {
    throw new Error('Consensus failure when registering mint from pool');
  }
  return deposit;
}

export const getNetworkDisplayName = (networkKey: string): string => {
  const networkNames: Record<string, string> = {
    'litvm': 'LitVM Testnet (zkLTC)',
    'bsc': 'Binance Smart Chain (BSC)',
    'polygon': 'Polygon (POL)',
    'tbnb': 'BSC Testnet (tBNB)',
  };
  return networkNames[networkKey] || networkKey;
};

export interface ConsensusResult {
  depositAddress: string | null;
  goodNodes: number[];
  consensusCount: number;
  hasConsensus: boolean;
}

export const queryDepositAddressWithConsensus = async (
  network: NetworkConfig,
  mintAddress: string,
  aliveNodes: number[]
): Promise<ConsensusResult> => {
  if (aliveNodes.length === 0) {
    return {
      depositAddress: null,
      goodNodes: [],
      consensusCount: 0,
      hasConsensus: false
    };
  }

  // Query all alive nodes for the deposit address
  const nodeResponses = await Promise.allSettled(
    aliveNodes.map(async (nodeIndex) => {
      const node = network.authorityNodes[nodeIndex];
      try {
        const response = await post(`${authorityBaseUrl(network, node)}/queryMintBalance`, authorityPostBody(network, {
          mintAddress
        }));
        return {
          nodeIndex,
          depositAddress: response.data?.depositAddress || null,
          data: response.data
        };
      } catch (error) {
        return {
          nodeIndex,
          depositAddress: null,
          data: null,
          error
        };
      }
    })
  );

  // Extract successful responses
  const successfulResponses = nodeResponses
    .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
    .map(result => result.value)
    .filter(response => response.depositAddress !== null);

  if (successfulResponses.length === 0) {
    return {
      depositAddress: null,
      goodNodes: [],
      consensusCount: 0,
      hasConsensus: false
    };
  }

  // Group responses by deposit address
  const addressGroups = new Map<string, { nodes: number[], data: any }>();
  
  successfulResponses.forEach(response => {
    const address = response.depositAddress;
    if (!addressGroups.has(address)) {
      addressGroups.set(address, { nodes: [], data: response.data });
    }
    addressGroups.get(address)!.nodes.push(response.nodeIndex);
  });

  // Find the address with the most consensus
  let bestConsensus = {
    depositAddress: null as string | null,
    goodNodes: [] as number[],
    consensusCount: 0
  };

  addressGroups.forEach((group, address) => {
    if (group.nodes.length > bestConsensus.consensusCount) {
      bestConsensus = {
        depositAddress: address,
        goodNodes: group.nodes,
        consensusCount: group.nodes.length
      };
    }
  });

  // Check if we have sufficient consensus (meets threshold)
  const hasConsensus = bestConsensus.consensusCount >= network.authorityThreshold;

  return {
    depositAddress: hasConsensus ? bestConsensus.depositAddress : null,
    goodNodes: hasConsensus ? bestConsensus.goodNodes : [],
    consensusCount: bestConsensus.consensusCount,
    hasConsensus
  };
};

export const getConsensusAuthorityLink = (network: NetworkConfig, goodNodes: number[]): string => {
  if (goodNodes.length === 0) {
    // Fallback to any available node if no consensus
    return getRandomAuthorityLink(network);
  }
  
  const nodeIndex = goodNodes[Math.floor(Math.random() * goodNodes.length)];
  const node = network.authorityNodes[nodeIndex];
  return authorityBaseUrl(network, node);
}; 